import {Component, inject, signal, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {map} from 'rxjs';
import {Popover} from 'primeng/popover';
import {LayoutService} from './services/app.layout.service';
import {RoleService} from '../shared/services/role.service';
import {AuthService} from '../auth/services/auth.service';
import {LoggerService} from '../shared/services/logger.service';
import {PermissionService} from '../shared/services/permission.service';
import {calendarDateToApiDay} from '../shared/components/calendar/calendar.component';
import {CalendarEventDto} from '../shared/dto';
import {readAgenda} from '../event/store/event.actions';
import {selectAgendaEvents} from '../event/store/event.selectors';

@Component({
  selector: 'app-top-bar-component',
  templateUrl: './app.top-bar.component.html',
  standalone: false,
})
export class AppTopBarComponent {
  protected readonly logger = inject(LoggerService);
  private authService = inject(AuthService);
  private readonly store = inject(Store);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly calendarPanel = viewChild<Popover>('calendarPanel');

  layoutService = inject(LayoutService);
  protected readonly roleService = inject(RoleService);

  /** Only operators who can read assignments see the agenda. */
  readonly canViewCalendar = this.permissionService.hasPermission('ASSIGNMENT_READ');

  /** Operator-wide events, from the agenda slot of the shared `event` store. */
  readonly agendaEvents = toSignal(
    this.store.select(selectAgendaEvents).pipe(map((list) => list ?? [])),
    { initialValue: [] as CalendarEventDto[] },
  );

  /** Day shown in the agenda popover — refreshed each time it opens. */
  readonly agendaDate = signal(new Date());

  onCalendarPanelShow(): void {
    this.agendaDate.set(new Date());
    if (!this.canViewCalendar) return;
    const day = calendarDateToApiDay(this.agendaDate());
    // Backend `BETWEEN start AND end` is inclusive on both ends — same-day
    // bounds so tomorrow's sessions are not pulled in as "nearest".
    this.store.dispatch(readAgenda({ start: day, end: day }));
  }

  onAgendaEventClick(event: CalendarEventDto): void {
    this.calendarPanel()?.hide();
    const userId = event.userId;
    if (userId == null || !this.permissionService.hasPermission('USER_READ')) return;
    this.router.navigate(['/app/users', userId])
      .catch((e) => this.logger.error(this.constructor.name, e));
  }

  toggleDarkMode() {
    this.layoutService.config.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
  }

  logout() {
    this.authService.logout(true, 'auth/login');
  }
}
